import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ProductsSelect from '../../../Products/ProductsSelect';
import type { ProductSelectOption } from '../../../Products/types';
import ProductSetsSelect, {
    type ProductSetSelectOption,
} from '../../../ProductsSets/ProductSetsSelect';

type MontageSource = 'product' | 'set';

interface ServiceMontageNameProps {
    disabled?: boolean;
    onNameChange: (name: string) => void;
}

const ServiceMontageName = ({ disabled = false, onNameChange }: ServiceMontageNameProps) => {
    const { t } = useTranslation(['Services']);
    const [sourceType, setSourceType] = useState<MontageSource>('product');
    const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
    const [selectedSetId, setSelectedSetId] = useState<number | null>(null);

    const applyName = (label: string) => {
        const trimmed = String(label || '').trim();
        if (!trimmed) return;
        onNameChange(`${t('fields.montagePrefix')} ${trimmed}`);
    };

    const handleSourceChange = (next: MontageSource) => {
        if (next === sourceType) return;
        setSourceType(next);
        setSelectedProductId(null);
        setSelectedSetId(null);
    };

    const handleProductChange = (opt: ProductSelectOption | null) => {
        setSelectedProductId(opt?.value ?? null);
        if (opt?.label) applyName(opt.label);
    };

    const handleSetChange = (opt: ProductSetSelectOption | null) => {
        setSelectedSetId(opt?.value ?? null);
        if (opt?.label) applyName(opt.label);
    };

    return (
        <div className="mb-2">
            <div className="btn-group w-100 mb-2">
                <Button
                    type="button"
                    size="sm"
                    variant={sourceType === 'product' ? 'primary' : 'outline-primary'}
                    disabled={disabled}
                    onClick={() => handleSourceChange('product')}
                >
                    <i className="bi bi-box-seam me-1"></i>
                    {t('fields.montageProduct')}
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant={sourceType === 'set' ? 'primary' : 'outline-primary'}
                    disabled={disabled}
                    onClick={() => handleSourceChange('set')}
                >
                    <i className="bi bi-collection me-1"></i>
                    {t('fields.montageSet')}
                </Button>
            </div>

            {sourceType === 'product' ? (
                <ProductsSelect
                    title={t('fields.montageSelectProduct')}
                    required={false}
                    disabled={disabled}
                    value={selectedProductId}
                    onChange={handleProductChange}
                />
            ) : (
                <ProductSetsSelect
                    title={t('fields.montageSelectSet')}
                    required={false}
                    disabled={disabled}
                    value={selectedSetId}
                    onChange={handleSetChange}
                />
            )}
        </div>
    );
};

export default ServiceMontageName;
